import {
  Group, Mesh, PlaneGeometry, ShaderMaterial, MeshBasicMaterial,
  Vector2, Vector3, CatmullRomCurve3,
} from 'three';
import vertSrc from '../shaders/fractal.vert.glsl?raw';
import fragSrc from '../shaders/fractal.frag.glsl?raw';
import { PALETTES, paletteNameFromId } from './palettes.js';

// Pure helpers — exported so we can test the audio→uniform mapping without
// instantiating three.js / WebGL.
export function powerFromBeat(beatPulse) {
  return 6.0 + (beatPulse || 0) * 4.0;
}

export function radiusFromBass(bassSpring) {
  return 3.0 - (bassSpring || 0) * 1.2;
}

export function paletteIndexFromAudio(highMidsSpring, flux) {
  return 0.5 + (highMidsSpring || 0) * 0.3 + (flux || 0) * 0.2;
}

export function camYFromMids(t, midsSpring) {
  return 0.5 + Math.sin((t || 0) * 0.3) * 0.3 + (midsSpring || 0) * 0.5;
}

export function rollFromAudio(t, highMidsSpring) {
  return Math.sin((t || 0) * 0.25) * 0.2 + (highMidsSpring || 0) * 0.4;
}

export function decayImpulse(prev, decay) {
  return (prev || 0) * (decay || 0);
}

const FRACTAL_NAMES = ['mandelbulb', 'sacred-lattice', 'lava-lamp', 'pointilized'];
// PR-C3: added 'stabilized' — centered axis-rotation, no audio camera
// movement. Audio still drives shape/color via surface-displacement.
const CAMERA_MODES = ['fly', 'orbit', 'stabilized'];

// Pure: speed along the fly-through curve as a function of audio signals.
// Baseline drift so silent moments still travel; lowMids/mids ramp into a
// punchy lurch on bass-heavy or busy sections.
export function speedFromAudio(lowMidsSpring, midsSpring) {
  return 0.015 + (lowMidsSpring || 0) * 0.04 + (midsSpring || 0) * 0.03;
}

// Pure: section-change warp. Adds 0.25..0.75 to current t, wraps to [0,1).
// Random in [0,1) supplied by caller so test can pin it.
export function nextFlyTOnSection(t, rand) {
  const r = (typeof rand === 'number') ? rand : 0;
  return (((t || 0) + 0.25 + r * 0.5) % 1.0 + 1.0) % 1.0;
}

// Pure: 8-point closed Catmull-Rom path. Mixes close (~1.0) and far (~2.5)
// points so the camera dives past and pulls back. Scale tightened from the
// prior 1.4..3.5 so the fractal stays inside the view frustum throughout the
// loop — the previous radius let the camera roam too far and the SDF would
// shrink to a speck or leave frame entirely between sections.
const FLY_PATH_POINTS = [
  [1.0, 0.2, 0.4], [2.0, -0.5, 1.5], [-0.3, -1.1, 2.3], [-1.7, 0.3, 1.3],
  [-0.7, 1.2, -0.4], [0.4, 1.7, -1.4], [0.9, 0.6, -2.2], [1.3, -0.3, -0.7],
];

function makeFlyPath() {
  return new CatmullRomCurve3(
    FLY_PATH_POINTS.map(([x, y, z]) => new Vector3(x, y, z)),
    true,
    'catmullrom',
    0.4,
  );
}

// Pure: wrap a t value plus a look-ahead delta into [0, 1).
// Used so the camera looks where it's GOING rather than dead-center origin.
export function lookAheadT(t, delta = 0.04) {
  const d = (typeof delta === 'number') ? delta : 0.04;
  return (((t || 0) + d) % 1 + 1) % 1;
}

// Pure: bias look-target toward the origin (where the fractal lives) so the
// camera never aims fully off-frame. Previous look-ahead-only logic let the
// camera face a point on the curve that was nowhere near the SDF, causing
// the fractal to drift out of view. originBias of 0.6 keeps the fractal
// roughly centered while preserving forward motion cues from the curve.
export function biasTowardOrigin(curveLookAhead, originBias = 0.6) {
  const b = Math.max(0, Math.min(1, typeof originBias === 'number' ? originBias : 0.6));
  return {
    x: curveLookAhead.x * (1 - b),
    y: curveLookAhead.y * (1 - b),
    z: curveLookAhead.z * (1 - b),
  };
}

// Pure: breathing scale multiplier for SDF "growing/detaching/reabsorbing".
// Slow sin breath modulated by lowEnergy phase. Bass no longer drives size
// directly (it now drives color via hueShiftFromBass) — bass-pulsate read
// as twitchy; balloon swell is the smoother low-frequency size signal.
export function breatheScale(t, lowEnergy /* , bassSpring */) {
  const phase = (t || 0) * 0.4 + (lowEnergy || 0) * 6.0;
  return 1.0 + Math.sin(phase) * 0.12;
}

// Pure: bass -> palette hue rotation in radians. The user wants bass to
// modulate COLOR not size. Idle baseline of 0 (no shift); strong bass swings
// up to ~1.0 rad (~60°) of hue rotation, sin-eased so the shift breathes.
export function hueShiftFromBass(bassSpring, beatPulse) {
  const drive = Math.min(1, (bassSpring || 0) * 0.9 + (beatPulse || 0) * 0.6);
  return Math.sin(drive * Math.PI * 0.5) * 1.0;
}

// Pure: sub-bass / sustained low-end -> smooth uniform balloon swell. Slow
// signal (bassSpring is critically-damped) so the blob pushes out gently
// instead of pulsing on transients. Output ~0..0.6 (shader caps the effect).
export function balloonSwellFromSubBass(bassSpring, lowEnergy) {
  return Math.min(0.6, (bassSpring || 0) * 0.4 + (lowEnergy || 0) * 0.35);
}

// Pure: mids -> spike amplitude. Mids carry vocal/snare body — the more
// midrange energy, the deeper the radial pinch that creates spikes.
export function spikeAmpFromMids(midsSpring, beatPulse) {
  return Math.min(0.7, (midsSpring || 0) * 0.6 + (beatPulse || 0) * 0.3);
}

// Pure: highs -> spike frequency (count). Treble pushes the angular noise
// frequency higher so hihat/cymbal hits sprout more numerous narrow points.
// Range 4..14: low treble = few broad bumps, high treble = many sharp spikes.
export function spikeFreqFromHighs(trebleSpring, highMidsSpring) {
  return 4.0 + (trebleSpring || 0) * 8.0 + (highMidsSpring || 0) * 2.0;
}

// PR-C1: per-band surface displacement amounts. Each uses ONLY its own
// spring-smoothed band so the visual motion lags the audio smoothly
// (no jitter on transients). Bass spring is the smoothest; treble the
// most reactive but still damped. Outputs clamped to 0..1; the shader
// multiplies by a small constant so the surface offset stays subtle.
export function surfBassFromAudio(bassSpring, lowEnergy) {
  return Math.min(1, (bassSpring || 0) * 0.85 + (lowEnergy || 0) * 0.35);
}
export function surfMidsFromAudio(midsSpring, lowMidsSpring) {
  return Math.min(1, (midsSpring || 0) * 0.7 + (lowMidsSpring || 0) * 0.4);
}
export function surfTrebleFromAudio(trebleSpring, highMidsSpring) {
  return Math.min(1, (trebleSpring || 0) * 0.8 + (highMidsSpring || 0) * 0.45);
}

// Per-fractal surface profile: 0 = rounded balloon (lava), 1 = sharp
// pointed spikes (sacred-lattice / pointilized). Mandelbulb sits at 0.5
// for a hybrid look that suits its mixed curvature.
export function surfProfileForFractal(fractalId) {
  const id = ((Number(fractalId) || 0) % 4 + 4) % 4;
  // 0=mandelbulb, 1=sacred-lattice, 2=lava, 3=pointilized (PR-C3, default to 1.0)
  if (id === 0) return 0.5;
  if (id === 1) return 0.9;
  if (id === 2) return 0.0;
  return 1.0;
}

// Pure: chromatic-aberration intensity from audio. Idle floor 0.2 keeps a
// subtle shimmer; bass / beat / flux push the RGB divergence higher. Capped
// at 1.2 so the effect never washes out the underlying color.
export function chromaFromAudio(bassSpring, beatPulse, flux) {
  return Math.min(
    1.2,
    0.2 + (bassSpring || 0) * 0.4 + (beatPulse || 0) * 0.5 + (flux || 0) * 0.3,
  );
}

// PR-C6: minimum camera radius for lava-lamp so the camera never gets
// close enough that a single blob fills the screen. Baseline 3.5 keeps
// the largest blob at ~60% of frame height; bass / lowEnergy push the
// safety bubble out further because the surface-displacement system
// (PR-C1) swells blob surfaces on heavy bass. Capped at 5.5.
export function lavaSafeMinRadius(bassSpring, lowEnergy) {
  return Math.min(
    5.5,
    3.5 + (bassSpring || 0) * 0.9 + (lowEnergy || 0) * 0.5,
  );
}

// PR-C6: project a camera position outward to at least `minR` from origin.
// Pure; tested without three.js. Returns the same {x,y,z} when already
// safe, else a scaled-out copy along the same direction.
export function pushOutsideRadius(x, y, z, minR) {
  const r2 = x * x + y * y + z * z;
  if (r2 >= minR * minR) return { x, y, z };
  const r = Math.sqrt(Math.max(r2, 1e-6));
  const k = minR / r;
  return { x: x * k, y: y * k, z: z * k };
}

// Pure id→name lookup so it's testable without three.js.
export function fractalNameFromId(id) {
  const n = ((Number(id) || 0) % FRACTAL_NAMES.length + FRACTAL_NAMES.length) % FRACTAL_NAMES.length;
  return FRACTAL_NAMES[n];
}

const DEFAULT_PALETTE = [
  new Vector3(0.95, 0.4, 0.2),
  new Vector3(0.2, 0.5, 0.95),
  new Vector3(0.6, 0.3, 0.8),
];

export class Fractal {
  constructor(opts = {}) {
    this.root = new Group();
    this._orbitAngle = 0;
    this._fractalSpin = 0;
    this._fractalSpinX = 0;
    this._fractalSpinZ = 0;
    this._powerDrift = 0;
    this._spikeMult = 1;
    this._bassImpact = 1;
    this._midsImpact = 1;
    this._trebleImpact = 1;
    this._paletteShift = 0;
    this._zoomImpulse = 0;
    this._shakeAmt = 0;
    this._fallback = false;
    this._cathedralOn = false;
    this._cathedralRipple = 0;
    this._paletteId = ((Number(opts.initialPaletteId) || 0) % PALETTES.length
      + PALETTES.length) % PALETTES.length;
    const initialFractalId = ((Number(opts.initialFractalId) || 0) % 4 + 4) % 4;

    // Fly-through state.
    this._flyT = 0;
    this._lastSectionId = null;
    this._flyPath = makeFlyPath();
    this._flyTarget = new Vector3(0, 0, 0);
    const VALID_INITIAL_VIEWS = ['fly', 'orbit', 'stabilized'];
    this._cameraMode = VALID_INITIAL_VIEWS.includes(opts.initialView) ? opts.initialView : 'fly';

    try {
      const w = (typeof window !== 'undefined') ? window.innerWidth : 1920;
      const h = (typeof window !== 'undefined') ? window.innerHeight : 1080;
      this.material = new ShaderMaterial({
        vertexShader: vertSrc,
        fragmentShader: fragSrc,
        uniforms: {
          u_resolution: { value: new Vector2(w, h) },
          u_time: { value: 0 },
          u_camPos: { value: new Vector3(0, 0.5, 3) },
          u_camTarget: { value: new Vector3(0, 0, 0) },
          u_camRoll: { value: 0 },
          u_power: { value: 8.0 },
          u_bass: { value: 0 },
          u_mids: { value: 0 },
          u_lowMids: { value: 0 },
          u_highMids: { value: 0 },
          u_treble: { value: 0 },
          u_flux: { value: 0 },
          u_beatPulse: { value: 0 },
          u_onsetPulse: { value: 0 },
          u_lowEnergy: { value: 0 },
          u_fractalSpin: { value: 0 },
          u_fractalSpinX: { value: 0 },
          u_fractalSpinZ: { value: 0 },
          u_breathe: { value: 1 },
          u_powerDrift: { value: 0 },
          u_paletteShift: { value: 0 },
          u_warp: { value: 0 },
          u_chroma: { value: 0.2 },
          u_spikeAmp: { value: 0 },
          u_spikeFreq: { value: 4 },
          u_balloonSwell: { value: 0 },
          u_hueShift: { value: 0 },
          u_kaleidoFolds: { value: 6 },
          u_kaleidoMix: { value: 1.0 },
          u_intensity: { value: 1.0 },
          u_trail: { value: 0.92 },
          u_breatheAmp: { value: 1.0 },
          u_chromaCap: { value: 1.0 },
          u_freqBalance: { value: 0.5 },
          u_paletteStrength: { value: 0.0 },
          u_fractalId: { value: initialFractalId },
          u_palette: { value: DEFAULT_PALETTE.map((v) => v.clone()) },
          u_surfBass: { value: 0 },
          u_surfMids: { value: 0 },
          u_surfTreble: { value: 0 },
          u_surfProfile: { value: surfProfileForFractal(initialFractalId) },
          u_surfMode: { value: 1.0 },
          u_cathedral: { value: 0.0 },
          u_cathedralRipple: { value: 0.0 },
        },
      });
      this.mesh = new Mesh(new PlaneGeometry(2, 2), this.material);
      // Clip-space quad — three.js frustum check would cull it incorrectly.
      this.mesh.frustumCulled = false;
      this.root.add(this.mesh);
      this.setPalette(this._paletteId);
    } catch (err) {
      // Shader compile fail must not block the page boot — cathedral mode
      // (M4/M5 fallback) still has to work via the C hotkey.
      console.error('[fractal] shader failed', err);
      this._fallback = true;
      this.material = new MeshBasicMaterial({ color: 0x880000 });
      this.mesh = new Mesh(new PlaneGeometry(2, 2), this.material);
      this.mesh.frustumCulled = false;
      this.root.add(this.mesh);
    }
  }

  update(signals, t, frameMs) {
    if (this._fallback || !this.material.uniforms) return;
    const u = this.material.uniforms;
    const s = signals || {};
    const dt = (frameMs || 16) / 1000;

    // Beat-triggered zoom punch (lunge closer, ease back).
    if (s.beat === true) this._zoomImpulse = 1.0;
    this._zoomImpulse = decayImpulse(this._zoomImpulse, 0.92);

    // Onset-triggered camera shake.
    if (s.onset === true) this._shakeAmt = 1.0;
    this._shakeAmt = decayImpulse(this._shakeAmt, 0.88);

    if (this._cameraMode === 'fly') {
      this._updateFlythrough(u, s, t || 0, dt);
    } else if (this._cameraMode === 'stabilized') {
      this._updateStabilized(u, t || 0, s);
    } else {
      this._updateOrbit(u, s, t || 0, dt);
    }
    // Stabilized view: no audio-driven roll/shake. The camera position is
    // already pinned in _updateStabilized; force roll/shake to zero so
    // the frame stays dead-centered. Other modes keep audio-reactive roll.
    if (this._cameraMode === 'stabilized') {
      u.u_camRoll.value = 0;
      this._zoomImpulse = 0;
      this._shakeAmt = 0;
    } else {
      u.u_camRoll.value = rollFromAudio(t || 0, s.highMidsSpring);
    }

    // Continuous fractal spin on all 3 axes (independent of camera).
    // Multi-axis spin makes the bulb "turn in on itself" rather than flat-spinning.
    this._fractalSpin += dt * (0.2 + (s.lowMidsSpring || 0) * 0.5);
    this._fractalSpinX += dt * (0.13 + (s.bassSpring || 0) * 0.4);
    this._fractalSpinZ += dt * (0.09 + (s.midsSpring || 0) * 0.3);
    u.u_fractalSpin.value = this._fractalSpin;
    u.u_fractalSpinX.value = this._fractalSpinX;
    u.u_fractalSpinZ.value = this._fractalSpinZ;

    // Breathing scale — legacy whole-shape pulse. Kept available for the
    // breatheAmp knob (sets multiplier), but when surfMode=1 the shader
    // hard-pins b to 1.0 so the global pulse is disabled by default.
    const breatheBase = breatheScale(t || 0, s.lowEnergy);
    u.u_breathe.value = 1.0 + (breatheBase - 1.0) * u.u_breatheAmp.value;

    // Frequency-mapped morphology: bass shifts COLOR (legacy), and the
    // surface-displacement system (PR-C1) drives per-point bulges/spikes
    // per band so different regions of the shape move at different times.
    u.u_hueShift.value = hueShiftFromBass(s.bassSpring, s.beatPulse);
    // Legacy uniforms kept fed but ignored when u_surfMode=1.
    u.u_balloonSwell.value = balloonSwellFromSubBass(s.bassSpring, s.lowEnergy);
    u.u_spikeAmp.value = spikeAmpFromMids(s.midsSpring, s.beatPulse) * this._spikeMult;
    u.u_spikeFreq.value = spikeFreqFromHighs(s.trebleSpring, s.highMidsSpring);
    // PR-C1: per-band surface displacement (apply knob multipliers).
    const bImp0 = this._bassImpact;
    const mImp0 = this._midsImpact;
    const tImp0 = this._trebleImpact;
    u.u_surfBass.value = surfBassFromAudio(s.bassSpring, s.lowEnergy) * bImp0;
    u.u_surfMids.value = surfMidsFromAudio(s.midsSpring, s.lowMidsSpring) * mImp0;
    u.u_surfTreble.value = surfTrebleFromAudio(s.trebleSpring, s.highMidsSpring) * tImp0;

    // Slow continuous power morph.
    this._powerDrift += dt * 0.15;
    u.u_powerDrift.value = this._powerDrift;

    // Palette cycle.
    this._paletteShift = (this._paletteShift
      + dt * (0.05 + (s.highMidsSpring || 0) * 0.3 + (s.flux || 0) * 0.2)) % 1.0;
    u.u_paletteShift.value = this._paletteShift;

    u.u_warp.value = (s.bassSpring || 0) * 0.3 + (s.beatPulse || 0) * 0.5;
    u.u_chroma.value = chromaFromAudio(s.bassSpring, s.beatPulse, s.flux);

    // Band-impact knob multipliers (default 1.0). Scale the audio signals
    // before they reach the shader so users can dial how much bass / mids /
    // treble drive the visualizer respectively.
    const bImp = this._bassImpact;
    const mImp = this._midsImpact;
    const tImp = this._trebleImpact;

    // PR-C4: mirror-cathedral. Beat/onset triggers a ripple that decays
    // each frame. Bass keeps the ripple alive between beats so the
    // chamber breathes with the music.
    if (s.beat === true || s.onset === true) this._cathedralRipple = 1.0;
    this._cathedralRipple = Math.max(
      (s.bassSpring || 0) * 0.35,
      this._cathedralRipple * 0.88,
    );
    u.u_cathedral.value = this._cathedralOn ? 1.0 : 0.0;
    u.u_cathedralRipple.value = this._cathedralRipple;

    u.u_time.value = t || 0;
    u.u_power.value = powerFromBeat(s.beatPulse) * bImp;
    u.u_bass.value = (s.bass || 0) * bImp;
    u.u_mids.value = (s.mids || 0) * mImp;
    u.u_lowMids.value = (s.lowMidsSpring || 0) * bImp;
    u.u_highMids.value = (s.highMidsSpring || 0) * tImp;
    u.u_treble.value = (s.trebleSpring || 0) * tImp;
    u.u_flux.value = s.flux || 0;
    u.u_beatPulse.value = (s.beatPulse || 0) * bImp;
    u.u_onsetPulse.value = s.onsetPulse || 0;
    u.u_lowEnergy.value = s.lowEnergy || 0;
  }

  // PR-C3: Stabilized view — centered, constant-radius axis rotation. No
  // audio influences position; the fractal stays dead-centered while the
  // surface-displacement system still drives shape/color from audio.
  _updateStabilized(u, t, s) {
    // Constant slow rotation (~one full revolution per 25s).
    this._orbitAngle += 0.0042;
    let r = 2.6;
    // PR-C6: lava gets pushed back further so a full blob is always
    // visible. Other fractals keep the cozy 2.6 framing.
    if ((u.u_fractalId?.value ?? 0) === 2) {
      r = lavaSafeMinRadius(s?.bassSpring, s?.lowEnergy);
    }
    u.u_camPos.value.set(
      Math.sin(this._orbitAngle) * r,
      0.0,
      Math.cos(this._orbitAngle) * r,
    );
    u.u_camTarget.value.set(0, 0, 0);
  }

  _updateOrbit(u, s, t, dt) {
    // Orbit: faster on lowMids + constant baseline so silence still drifts.
    this._orbitAngle += (s.lowMidsSpring || 0) * dt * 1.2 + dt * 0.15;
    const r = radiusFromBass(s.bassSpring) - this._zoomImpulse * 0.6;
    const camY = camYFromMids(t, s.midsSpring);
    const sx = (Math.random() - 0.5) * this._shakeAmt * 0.15;
    const sy = (Math.random() - 0.5) * this._shakeAmt * 0.15;
    let cx = Math.sin(this._orbitAngle) * r + sx;
    let cy = camY + sy;
    let cz = Math.cos(this._orbitAngle) * r;
    // PR-C6: lava safety — never let orbit dip the camera inside a blob.
    if ((u.u_fractalId?.value ?? 0) === 2) {
      const minR = lavaSafeMinRadius(s.bassSpring, s.lowEnergy);
      const safe = pushOutsideRadius(cx, cy, cz, minR);
      cx = safe.x; cy = safe.y; cz = safe.z;
    }
    u.u_camPos.value.set(cx, cy, cz);
    u.u_camTarget.value.set(0, 0, 0);
  }

  _updateFlythrough(u, s, t, dt) {
    // Section change -> jump to a fresh segment for a dramatic scene cut.
    const sid = s.sectionId;
    if (this._lastSectionId === null) {
      this._lastSectionId = sid ?? 0;
    } else if (sid !== undefined && sid !== this._lastSectionId) {
      this._flyT = nextFlyTOnSection(this._flyT, Math.random());
      this._lastSectionId = sid;
    }

    const speed = speedFromAudio(s.lowMidsSpring, s.midsSpring);
    // Beat zoom impulse also goosed forward velocity for an extra "lunge".
    const boost = this._zoomImpulse * 0.05;
    this._flyT = (this._flyT + dt * (speed + boost)) % 1.0;
    if (this._flyT < 0) this._flyT += 1.0;

    // Bass beats slingshot the SAMPLED position forward briefly (dolly) so the
    // camera lurches ahead on impact without permanently advancing the cursor.
    const forwardBoost = (s.beatPulse || 0) * 0.08;
    const boostedT = ((this._flyT + forwardBoost) % 1.0 + 1.0) % 1.0;
    const pos = this._flyPath.getPointAt(boostedT);
    const sx = (Math.random() - 0.5) * this._shakeAmt * 0.15;
    const sy = (Math.random() - 0.5) * this._shakeAmt * 0.15;
    // PR-C5: lava field-of-blobs. The lava SDF fills the central region;
    // when the fly path dips inward the camera washes out inside a blob.
    // Push the camera outward to a safe minimum radius so a blob edge is
    // always visible. Applied to lava only; other fractals keep the
    // original close-flyby drama.
    const fractalId = u.u_fractalId?.value ?? 0;
    let cx = pos.x + sx;
    let cy = pos.y + sy;
    let cz = pos.z;
    if (fractalId === 2) {
      // PR-C6: SDF-aware safety bubble. Bumped from C5's 2.6 to a
      // dynamic 3.5+ (climbs with bass / lowEnergy) so the largest
      // blob in frame never exceeds ~60% of screen height. Uses the
      // pure helper so tests can verify the math without WebGL.
      const minR = lavaSafeMinRadius(s.bassSpring, s.lowEnergy);
      const safe = pushOutsideRadius(cx, cy, cz, minR);
      cx = safe.x; cy = safe.y; cz = safe.z;
    }
    u.u_camPos.value.set(cx, cy, cz);

    // Look-ahead: aim the camera at a point further along the curve so it
    // reads as "flying past" the fractal instead of orbiting a fixed center.
    // Then bias 60% toward origin so the SDF (which lives at origin) stays
    // visibly in frame even when the curve pulls the camera far out.
    const aheadT = lookAheadT(boostedT, 0.04);
    const lookPos = this._flyPath.getPointAt(aheadT);
    const biased = biasTowardOrigin(lookPos, 0.6);
    const bp = s.beatPulse || 0;
    this._flyTarget.set(
      biased.x + (Math.random() - 0.5) * bp * 0.4,
      biased.y + (Math.random() - 0.5) * bp * 0.4,
      biased.z,
    );
    u.u_camTarget.value.copy(this._flyTarget);
  }

  get cameraMode() {
    return this._cameraMode;
  }

  setCameraMode(mode) {
    if (CAMERA_MODES.indexOf(mode) >= 0) {
      this._cameraMode = mode;
    } else {
      this._cameraMode = 'fly';
    }
    return this._cameraMode;
  }

  // PR-C4: mirror-cathedral. Boolean on/off; when on, the fractal's BG
  // becomes an infinite mirror chamber with beat-driven ripples.
  setCathedral(on) {
    this._cathedralOn = !!on;
    return this._cathedralOn;
  }

  toggleCathedral() {
    this._cathedralOn = !this._cathedralOn;
    return this._cathedralOn;
  }

  get cathedralOn() {
    return this._cathedralOn;
  }

  cycleCameraMode() {
    const i = CAMERA_MODES.indexOf(this._cameraMode);
    const next = CAMERA_MODES[(i + 1) % CAMERA_MODES.length];
    this._cameraMode = next;
    return next;
  }

  setFractal(id) {
    if (this.material?.uniforms?.u_fractalId) {
      const next = ((Number(id) || 0) % 4 + 4) % 4;
      this.material.uniforms.u_fractalId.value = next;
      if (this.material.uniforms.u_surfProfile) {
        this.material.uniforms.u_surfProfile.value = surfProfileForFractal(next);
      }
    }
  }

  getFractalName() {
    const id = this.material?.uniforms?.u_fractalId?.value ?? 0;
    return fractalNameFromId(id);
  }

  cycleFractal() {
    const cur = this.material?.uniforms?.u_fractalId?.value ?? 0;
    const next = (cur + 1) % 4;
    this.setFractal(next);
    return this.getFractalName();
  }

  setPalette(id) {
    const n = ((Number(id) || 0) % PALETTES.length + PALETTES.length) % PALETTES.length;
    this._paletteId = n;
    const stops = PALETTES[n].stops;
    const u = this.material?.uniforms?.u_palette;
    if (!u || !Array.isArray(u.value)) return;
    for (let i = 0; i < 3; i += 1) {
      const s = stops[i];
      u.value[i].set?.(s[0], s[1], s[2]);
    }
  }

  setCustomPalette(stops) {
    // stops: [[r,g,b], [r,g,b], [r,g,b]] in 0..1
    const u = this.material?.uniforms?.u_palette;
    if (!u || !Array.isArray(stops) || stops.length < 3) return;
    for (let i = 0; i < 3; i += 1) {
      const [r, g, b] = stops[i] || [1, 1, 1];
      u.value[i].set(
        Math.max(0, Math.min(1, Number(r))),
        Math.max(0, Math.min(1, Number(g))),
        Math.max(0, Math.min(1, Number(b))),
      );
    }
    this._paletteId = -1;
  }

  cyclePalette() {
    this.setPalette((this._paletteId + 1) % PALETTES.length);
    return this.currentPaletteName;
  }

  get currentPaletteName() {
    return paletteNameFromId(this._paletteId);
  }

  get currentFractalId() {
    return this.material?.uniforms?.u_fractalId?.value ?? 0;
  }

  setResolution(w, h) {
    if (this.material?.uniforms?.u_resolution) {
      this.material.uniforms.u_resolution.value.set(w, h);
    }
  }

  _setUniformClamped(name, v, lo, hi) {
    const u = this.material?.uniforms?.[name];
    if (!u) return;
    const n = Number(v);
    const clamped = Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo));
    u.value = clamped;
  }

  setIntensity(v) { this._setUniformClamped('u_intensity', v, 0, 2); }
  setTrail(v) { this._setUniformClamped('u_trail', v, 0, 1); }
  setBreatheAmp(v) { this._setUniformClamped('u_breatheAmp', v, 0, 2); }
  setChromaCap(v) { this._setUniformClamped('u_chromaCap', v, 0, 2); }
  setFreqBalance(v) { this._setUniformClamped('u_freqBalance', v, 0, 1); }
  setPaletteStrength(v) { this._setUniformClamped('u_paletteStrength', v, 0, 1); }
  setKaleidoFolds(v) {
    const u = this.material?.uniforms?.u_kaleidoFolds;
    if (!u) return;
    const n = Math.round(Number(v));
    u.value = Math.max(2, Math.min(12, Number.isFinite(n) ? n : 6));
  }
  // u_spikeAmp is set every frame from audio; this stores a knob multiplier
  // we apply in update(). Falls through to the uniform via update().
  setBassImpact(v) {
    const n = Number(v);
    this._bassImpact = Math.max(0, Math.min(2, Number.isFinite(n) ? n : 1));
  }
  setMidsImpact(v) {
    const n = Number(v);
    this._midsImpact = Math.max(0, Math.min(2, Number.isFinite(n) ? n : 1));
  }
  setTrebleImpact(v) {
    const n = Number(v);
    this._trebleImpact = Math.max(0, Math.min(2, Number.isFinite(n) ? n : 1));
  }
  setSpikeMult(v) {
    const n = Number(v);
    this._spikeMult = Math.max(0, Math.min(2, Number.isFinite(n) ? n : 1));
  }

  dispose() {
    if (this.mesh?.geometry) this.mesh.geometry.dispose();
    if (this.material?.dispose) this.material.dispose();
    this.root.clear?.();
  }
}
